import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cidrSchema } from './utils';
import { IPIFY_API } from '../../../apis/externalApis';
import { DEFAULT_SUBNET_MASK } from '../../../constants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { sentenceCase } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import TextInput from '../../elements/text-input/TextInput';
import Modal from '../../sections/modal/Modal';

const AddCidrButton = ({ instance, instanceBusy, maxCidrLimit, onAddCidr }) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const toggleModal = () => setOpen(!open);

  const cidrList = instance.cidrs;
  const rawCidrList = cidrList.length === 0 ? [] : cidrList.map((c) => c.cidr);
  const formattedCidrList =
    cidrList.length === 0 ? [] : cidrList.map((c) => ({ cidr: c.cidr }));

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(cidrSchema(rawCidrList)),
    shouldUnregister: true,
  });

  const generateCIDR = async () => {
    const { data: detectedIp } = await axios.get(IPIFY_API);
    setValue('cidr', detectedIp + DEFAULT_SUBNET_MASK, {
      shouldValidate: true,
    });
  };

  const handleAddNewCidr = async (form) => {
    try {
      const cidrList = [...formattedCidrList, { cidr: form.cidr }];
      onAddCidr(cidrList);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('addSuccess', {
            ns: 'notifications',
            text: 'CIDR',
          })
        )
      );
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('addError', {
            ns: 'notifications',
            text: 'CIDR',
          })
        )
      );
    } finally {
      toggleModal();
    }
  };

  return (
    <>
      <Button
        disabled={rawCidrList.length >= maxCidrLimit || instanceBusy}
        onClick={toggleModal}
        sx={{ alignSelf: 'flex-end' }}
      >
        <AddIcon />
        {t('add', {
          ns: 'buttons',
          text: t('CIDRRange', {
            ns: 'fields',
            text: `${maxCidrLimit - rawCidrList.length}`,
          }),
        })}
      </Button>
      <Modal
        action={
          <DialogActions>
            <Button
              variant="text"
              type="reset"
              onClick={toggleModal}
              aria-label="reset"
            >
              {t('cancel', { ns: 'buttons' })}
            </Button>
            <Button
              variant="text"
              onClick={generateCIDR}
              aria-label="generate CIDR"
            >
              {t('generateCIDR', { ns: 'buttons' })}
            </Button>
            <Button
              disabled={!isValid}
              onClick={handleSubmit(handleAddNewCidr)}
              aria-label="save"
            >
              {t('save', { ns: 'buttons' })}
            </Button>
          </DialogActions>
        }
        handleClose={toggleModal}
        openModal={open}
        title={t('addCidr', { ns: 'sectionTitles' })}
      >
        <Text sx={{ mb: 2 }}>{t('addCidr', { ns: 'descriptions' })}</Text>
        <TextInput
          name="cidr"
          autoComplete="off"
          control={control}
          errors={errors}
          label={t('ipRange', { ns: 'fields' })}
        />
      </Modal>
    </>
  );
};

AddCidrButton.propTypes = {
  instance: PropTypes.object.isRequired,
  instanceBusy: PropTypes.bool,
  maxCidrLimit: PropTypes.number.isRequired,
  onAddCidr: PropTypes.func.isRequired,
};

export default AddCidrButton;
